import { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Sparkles, Check, ShoppingBag, Code2, RefreshCw, Gift,
  MessageSquare, Wand2, Star, Clock,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { TOOL_SECTIONS, PACKAGES, BUY_MODES, DELIVERY } from "@/data/toolsCatalog";
import { getCatalog } from "@/lib/api";
import usePageSeo, { useBreadcrumbSchema } from "@/hooks/usePageSeo";
import { PAGE_SEO } from "@/data/pageSeo";

const MODEL_STYLE = {
  free: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: Gift },
  buy: { cls: "bg-blue-50 text-brand border-blue-200", Icon: ShoppingBag },
  embed: { cls: "bg-violet-50 text-violet-700 border-violet-200", Icon: Code2 },
  subscribe: { cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: RefreshCw },
};

const ModelBadge = ({ m }) => {
  const s = MODEL_STYLE[m]; if (!s) return null;
  const { Icon } = s;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${s.cls}`} title={DELIVERY[m]?.tip} data-testid={`model-badge-${m}`}>
      <Icon size={11} /> {DELIVERY[m]?.label}
    </span>
  );
};

const ToolCard = ({ t }) => (
  <div data-testid={`tool-card-${t.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    className={`relative bg-white border rounded-2xl p-5 card-shadow transition-all hover:-translate-y-1 flex flex-col ${t.highlight ? "border-brand/40 ring-1 ring-brand/10" : "border-slate-200 hover:border-slate-300"}`}>
    <div className="flex items-start justify-between gap-2">
      <h3 className="font-heading font-bold text-slate-900 leading-tight">{t.name}</h3>
      <div className="flex flex-col items-end gap-1 shrink-0">
        {t.isNew && <span className="text-[10px] font-bold uppercase tracking-wider bg-brand text-white px-2 py-0.5 rounded-full">New</span>}
        {t.comingSoon && <span className="text-[10px] font-semibold uppercase tracking-wider bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full inline-flex items-center gap-1"><Clock size={9} /> Soon</span>}
      </div>
    </div>
    <p className="text-sm text-slate-500 mt-1.5 leading-relaxed flex-1">{t.desc}</p>
    <div className="flex flex-wrap gap-1.5 mt-3">{t.models.map((m) => <ModelBadge key={m} m={m} />)}</div>
    {t.priceFrom && <div className="text-xs text-slate-400 mt-2">{t.priceFrom}</div>}
    <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
      {t.to ? (
        <Link to={t.to} className="text-xs font-semibold text-brand inline-flex items-center gap-1 hover:gap-2 transition-all" data-testid={`tool-explore-${t.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>Explore <ArrowRight size={13} /></Link>
      ) : <span />}
      <Link to="/ai-chatbot" className="text-xs font-semibold text-slate-500 hover:text-brand inline-flex items-center gap-1">Get a quote</Link>
    </div>
  </div>
);

export default function ToolsCatalog() {
  const [modelFilter, setModelFilter] = useState(null); // null | free|buy|embed|subscribe | "new"
  const [catalog, setCatalog] = useState({ sections: TOOL_SECTIONS, packages: PACKAGES });

  usePageSeo(PAGE_SEO["/tools"]);
  useBreadcrumbSchema([{ name: "Home", url: "/" }, { name: "Growth Platform", url: "/tools" }, { name: "Tools Catalog", url: "/tools" }]);

  useEffect(() => {
    getCatalog()
      .then((d) => { if (d?.sections?.length) setCatalog({ sections: d.sections, packages: d.packages || PACKAGES }); })
      .catch(() => { /* keep static fallback */ });
  }, []);

  const allSections = catalog.sections;
  const packages = catalog.packages;

  const sections = useMemo(() => {
    if (!modelFilter) return allSections;
    return allSections.map((s) => ({
      ...s,
      tools: s.tools.filter((t) => (modelFilter === "new" ? t.isNew : t.models.includes(modelFilter))),
    })).filter((s) => s.tools.length > 0);
  }, [modelFilter, allSections]);

  const newTools = useMemo(() => allSections.flatMap((s) => s.tools).filter((t) => t.isNew).slice(0, 8), [allSections]);

  const FilterChip = ({ value, label }) => (
    <button onClick={() => setModelFilter(value)} data-testid={`tools-filter-${value || "all"}`}
      className={`text-sm rounded-full px-4 py-2 border transition-colors ${modelFilter === value ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
      {label}
    </button>
  );

  return (
    <>
      <Navbar />
      <main data-testid="tools-catalog" className="bg-slate-50">
        {/* Hero */}
        <section className="pt-28 pb-12 bg-white border-b border-slate-100">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.18em] text-brand"><Sparkles size={13} /> Tools &amp; Packages</span>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 mt-4">Every tool we build — <span className="text-brand">your way.</span></h1>
              <p className="text-slate-500 mt-4 text-lg">Buy a tool outright, embed one on your existing site, or subscribe to a fully managed plan. Explore everything WebsiteTech can build, run and grow for you.</p>
              <div className="flex flex-wrap gap-3 mt-7">
                <Link to="/audit" data-testid="tools-hero-audit" className="inline-flex items-center gap-2 font-semibold rounded-xl px-6 py-3.5 bg-brand text-white hover:-translate-y-0.5 transition-transform">Get my free audit <ArrowRight size={16} /></Link>
                <Link to="/ai-chatbot" data-testid="tools-hero-ai" className="inline-flex items-center gap-2 font-semibold rounded-xl px-6 py-3.5 btn-outline"><MessageSquare size={16} /> Talk to WebsiteTech AI</Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Three ways to buy */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="grid md:grid-cols-3 gap-5">
            {BUY_MODES.map((b) => {
              const { Icon, cls } = MODEL_STYLE[b.key];
              return (
                <div key={b.key} className="bg-white border border-slate-200 rounded-2xl p-6 card-shadow" data-testid={`buy-mode-${b.key}`}>
                  <div className={`h-11 w-11 rounded-xl grid place-items-center border ${cls}`}><Icon size={20} /></div>
                  <h3 className="font-heading font-bold text-slate-900 mt-4 text-lg">{b.title}</h3>
                  <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{b.desc}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* New & offered separately */}
        <section className="py-14 bg-gradient-to-b from-blue-50/60 to-transparent">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2 mb-2"><Wand2 size={18} className="text-brand" /><span className="text-xs font-bold uppercase tracking-[0.18em] text-brand">New — available separately</span></div>
            <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">Fresh tools you can add without a rebuild</h2>
            <p className="text-slate-500 mt-2 max-w-2xl">Our newest tools can be embedded on the site you already own — like the AI Assistant, voice, booking and text-to-speech.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
              {newTools.map((t) => <ToolCard key={t.name} t={t} />)}
            </div>
          </div>
        </section>

        {/* Filter + all sections */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-wrap gap-2 mb-2" data-testid="tools-filters">
            <FilterChip value={null} label="All tools" />
            <FilterChip value="new" label="New" />
            <FilterChip value="free" label="Free" />
            <FilterChip value="buy" label="Buy once" />
            <FilterChip value="embed" label="Embed" />
            <FilterChip value="subscribe" label="Subscribe" />
          </div>

          {sections.map((s) => (
            <div key={s.id} className="pt-12" data-testid={`tool-section-${s.id}`}>
              <h2 className="font-heading text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900">{s.title}</h2>
              <p className="text-slate-500 mt-1.5 max-w-2xl">{s.blurb}</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
                {s.tools.map((t) => <ToolCard key={t.name} t={t} />)}
              </div>
            </div>
          ))}
        </section>

        {/* Managed packages */}
        <section className="py-16 bg-white border-t border-slate-100" data-testid="tools-packages">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto">
              <span className="text-xs font-bold uppercase tracking-[0.18em] text-brand">Managed plans</span>
              <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mt-3">Prefer we run it all for you?</h2>
              <p className="text-slate-500 mt-2">Pick a managed plan and WebsiteTech keeps your site improving every month.</p>
            </div>
            <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4 mt-10 items-start">
              {packages.map((p) => (
                <div key={p.name} data-testid={`package-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`relative rounded-2xl p-5 border flex flex-col ${p.popular ? "border-brand ring-1 ring-brand/20 bg-blue-50/40" : "border-slate-200 bg-white"} card-shadow`}>
                  {p.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-widest bg-brand text-white px-3 py-1 rounded-full inline-flex items-center gap-1"><Star size={10} /> Popular</span>}
                  <h3 className="font-heading font-bold text-slate-900">{p.name}</h3>
                  <div className="text-xs text-slate-400 mt-0.5">{p.tagline}</div>
                  <div className="text-brand font-bold mt-2">{p.priceFrom}</div>
                  <ul className="mt-4 space-y-2 flex-1">
                    {p.bullets.map((b) => <li key={b} className="flex items-start gap-2 text-sm text-slate-600"><Check size={14} className="text-brand mt-0.5 shrink-0" /> {b}</li>)}
                  </ul>
                  <Link to="/ai-chatbot" className={`mt-5 w-full text-center rounded-xl px-4 py-2.5 text-sm font-semibold ${p.popular ? "bg-brand text-white" : "btn-outline"}`}>Get a quote</Link>
                </div>
              ))}
            </div>
            <p className="text-center text-xs text-slate-400 mt-6 max-w-2xl mx-auto">Indicative pricing. Final pricing depends on scope and region. We never promise guaranteed rankings, traffic, AI citations, leads or revenue.</p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-3xl p-10 md:p-14 text-center bg-brand text-white">
              <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight">Not sure which tool you need?</h2>
              <p className="mt-3 text-white/80 max-w-xl mx-auto">Run a free 360° audit or ask our AI — we'll point you to the right tool or plan for your business.</p>
              <div className="flex flex-wrap justify-center gap-3 mt-7">
                <Link to="/audit" className="inline-flex items-center gap-2 font-semibold rounded-xl px-6 py-3.5 bg-white text-brand hover:-translate-y-0.5 transition-transform">Get my free audit <ArrowRight size={16} /></Link>
                <Link to="/ai-chatbot" className="inline-flex items-center gap-2 font-semibold rounded-xl px-6 py-3.5 border border-white/40 hover:bg-white/10 transition-colors"><MessageSquare size={16} /> Talk to WebsiteTech AI</Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
